import React, { forwardRef } from 'react'
import head from 'lodash/head'
import cloneDeep from 'lodash/cloneDeep'
import { makeStyles } from '@material-ui/styles'
import classnames from 'classnames'
import Button from 'tcweb-material-components/core/button'
import Image from 'tcweb-material-components/core/image'
import get from 'tcweb-material-components/core/poly/get'

import Link from '../../components/common/link'

const useStyles = makeStyles( ({ breakpoints }) => ({
    root: {
        [breakpoints.up('md')]: {
            width: ({ width }) => !!width ? `${width}%` : 'auto'
        }
    },
    partialWidth: {
        [breakpoints.up('md')]: {
            width: '50%'
        }
    },
    aligncenter: {
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    alignright: {
        marginLeft: 'auto',
        marginRight: 0
    },
    alignleft: {
        marginLeft: 0,
        marginRight: 'auto'
    },
    icon: {
        height: '1.33em',
        width: 'auto'
    }
}),
{ classNamePrefix: 'ButtonSwap' })

const getTitle = (child) => {
    // if found child with text, return it
    if (child.hasOwnProperty('data')) return child.data

    // Keep searching
    if (child.hasOwnProperty('children')) {
        for (let i in child.children) {
            return getTitle(child.children[i])
        }
    }
    // No more to search / didn't find
    else return null
}

const getTitleAndUrl = (children) => {
    const firstChild = head(children) || {}
    const childAttribs = firstChild.attribs || {}

    if ((childAttribs.class || '').includes('wp-block-button__link')) {
        return [getTitle(firstChild), childAttribs.href]
    }

    return [null, null]
}

const ButtonSwap = forwardRef(({ attribs, children, data, className }, ref) => {
    const match = get(attribs, 'class', '').match(/wp-block-button__width-(\w*)\b/)
    const _attribs = cloneDeep(attribs)
    const [title, url] = getTitleAndUrl(children)
    const _classes = useStyles({ width: get(match, '[1]') })
    const alignmentClass = head(get(_attribs, 'class', '').match(/aligncenter|alignleft|alignright/g)) // Remove from button component when WP is at version 5.4.1 on prod
    const target = get(children, '0.attribs.target', '')

    let color
    let disabled = false
    const fullWidth = _attribs.class.includes('full-width')

    if (_attribs.class.includes('action-primary')) {
        color = 'primary'
    } else if (_attribs.class.includes('action-secondary')) {
        color = 'secondary'
    } else if (_attribs.class.includes('action-tertiary')) {
        color = 'tertiary'
    } else {
        color = 'primary'
    }

    if (_attribs.class.includes('disabled')) {
        disabled = true
    }

    const buttonClassName = classnames(
        _classes.root,
        { [_classes.partialWidth]: !fullWidth },
        _classes[alignmentClass],
        _attribs.class.replace(/wp-block-button/g, '').replace(/\S*is-style-btn-action\S*/g, '')
    )

    const Icon = get(data, 'media.mobile.url') ? <Image className={_classes.icon} src={get(data, 'media.mobile.url')} role='presentation' /> : null

    const defaultProps = {
        color,
        title: get(_attribs, `['data-screen-reader-title']`, title),
        target,
        fullWidth,
        startIcon: Icon,
        classes: { root: classnames(buttonClassName, className) },
        variant: 'contained',
        telemetryName: _attribs['data-telemetry-name'] || _attribs['data-mobile-telemetry-name'], // data-mobile-telemetry-name -old, depricate once all buttons are redone in cms
        'aria-label': _attribs['data-screen-reader-title']
    }

    if (url) {
        return (
            <Link {...defaultProps} href={url} buttonRef={ref} disabled={disabled}>
                {title}
            </Link>
        )
    }

    return (
        <Button {...defaultProps} buttonRef={ref} disabled={disabled}>
            {title}
        </Button>
    )
})

export default ButtonSwap